<script setup lang="ts">
	import { Teleport, computed, nextTick, onBeforeUnmount, ref, useAttrs, useSlots, watch } from 'vue'

	defineOptions({
		inheritAttrs: false,
	})

	type ResolvedPlacement =
		| 'top'
		| 'bottom'
		| 'left'
		| 'right'
		| 'top-start'
		| 'top-end'
		| 'bottom-start'
		| 'bottom-end'
	type Placement = ResolvedPlacement | 'auto' | 'auto-start' | 'auto-end'
	type TriggerMode = 'click' | 'hover' | 'manual'

	interface FloaterSlotProps {
		open: () => void
		close: () => void
		toggle: () => void
		isOpen: boolean
	}

	interface Props {
		modelValue?: boolean
		disabled?: boolean
		hover?: boolean
		interactive?: boolean
		triggerMode?: TriggerMode
		anchor?: HTMLElement | null
		placement?: Placement
		offset?: number
		offsetDistance?: number | string
		teleport?: boolean | string
		portal?: boolean | string
		strategy?: 'fixed' | 'absolute'
		zIndex?: number | string
		preventOverflow?: boolean
		closeOnOutside?: boolean
		closeOnEsc?: boolean
		autoFocus?: boolean
		trapFocus?: boolean
		arrow?: boolean
		matchWidth?: boolean
		closeOnContentClick?: boolean
		openDelay?: number
		closeDelay?: number
		triggerTag?: string
		triggerClass?: string
		panelClass?: string
		contentClass?: string
	}

	const ARROW_SIZE = 8
	const VIEWPORT_PADDING = 16
	const ARROW_BORDER_COLOR = 'color-mix(in oklab, var(--color-neutral) 50%, transparent)'

	const props = withDefaults(defineProps<Props>(), {
		modelValue: undefined,
		disabled: false,
		hover: false,
		interactive: true,
		triggerMode: 'click',
		anchor: null,
		placement: 'bottom-start',
		offset: 8,
		offsetDistance: undefined,
		teleport: undefined,
		portal: true,
		strategy: 'fixed',
		zIndex: 1100,
		preventOverflow: true,
		closeOnOutside: true,
		closeOnEsc: true,
		autoFocus: true,
		trapFocus: false,
		arrow: true,
		matchWidth: false,
		closeOnContentClick: false,
		openDelay: 75,
		closeDelay: 120,
		triggerTag: 'span',
		triggerClass: 'inline-block',
		panelClass: '',
		contentClass: '',
	})

	const emit = defineEmits<{
		'update:modelValue': [value: boolean]
		open: []
		close: []
	}>()

	defineSlots<{
		default?: (props: FloaterSlotProps) => unknown
		content?: (props: FloaterSlotProps) => unknown
	}>()

	const attrs = useAttrs()
	const slots = useSlots()
	const triggerRef = ref<HTMLElement | null>(null)
	const panelRef = ref<HTMLElement | null>(null)
	const internalOpen = ref(false)
	const hoverPinned = ref(false)
	const panelStyle = ref<Record<string, string>>({})
	const arrowStyle = ref<Record<string, string>>({})
	const effectivePlacement = ref<ResolvedPlacement>('bottom-start')

	let openTimeoutId: number | null = null
	let closeTimeoutId: number | null = null
	let resizeObserver: ResizeObserver | null = null

	const hasTriggerSlot = computed(() => Boolean(slots.default))
	const hasContentSlot = computed(() => Boolean(slots.content))
	const isControlled = computed(() => props.modelValue !== undefined)
	const effectiveTriggerMode = computed<TriggerMode>(() => (props.hover ? 'hover' : props.triggerMode))
	const isOpen = computed(() => (isControlled.value ? Boolean(props.modelValue) : internalOpen.value))
	const defaultAnchorEl = computed<HTMLElement | null>(() => {
		const trigger = triggerRef.value
		if (!trigger) return null

		if (trigger.childElementCount === 1) {
			const firstChild = trigger.firstElementChild
			if (firstChild instanceof HTMLElement) return firstChild
		}

		return trigger
	})
	const anchorEl = computed<HTMLElement | null>(() => props.anchor || defaultAnchorEl.value)
	const resolvedOffset = computed<number>(() => {
		if (props.offsetDistance === undefined || props.offsetDistance === null || props.offsetDistance === '') {
			return props.offset
		}

		const parsedOffset = Number(props.offsetDistance)
		return Number.isFinite(parsedOffset) ? parsedOffset : props.offset
	})

	const teleportTarget = computed(() => {
		const target = props.teleport === undefined ? props.portal : props.teleport
		if (target === true) return 'body'
		if (typeof target === 'string' && target.trim()) return target
		return ''
	})

	const wrapperComponent = computed(() => (teleportTarget.value ? Teleport : null))
	const resolvedZIndex = computed(() => {
		if (props.zIndex === undefined || props.zIndex === null || props.zIndex === '') return undefined
		return String(props.zIndex)
	})

	const transitionName = computed(() => {
		const placement = effectivePlacement.value
		if (placement.startsWith('top')) return 'floater-slide-up'
		if (placement.startsWith('left')) return 'floater-slide-left'
		if (placement.startsWith('right')) return 'floater-slide-right'
		return 'floater-slide-down'
	})

	const clearOpenDelay = () => {
		if (openTimeoutId === null) return
		window.clearTimeout(openTimeoutId)
		openTimeoutId = null
	}

	const clearCloseDelay = () => {
		if (closeTimeoutId === null) return
		window.clearTimeout(closeTimeoutId)
		closeTimeoutId = null
	}

	const clearDelays = () => {
		clearOpenDelay()
		clearCloseDelay()
	}

	const disconnectObserver = () => {
		resizeObserver?.disconnect()
		resizeObserver = null
	}

	const setOpenState = (nextOpen: boolean) => {
		if (nextOpen && props.disabled) return
		if (nextOpen === isOpen.value) return

		if (!isControlled.value) {
			internalOpen.value = nextOpen
		}
		if (!nextOpen) {
			hoverPinned.value = false
		}

		emit('update:modelValue', nextOpen)
		emit(nextOpen ? 'open' : 'close')
	}

	const open = () => {
		clearCloseDelay()
		setOpenState(true)
	}

	const close = () => {
		clearOpenDelay()
		setOpenState(false)
	}

	const toggle = () => {
		if (isOpen.value) close()
		else open()
	}

	const scheduleOpen = () => {
		if (effectiveTriggerMode.value !== 'hover' || props.disabled) return
		if (hoverPinned.value) return
		clearCloseDelay()

		if (props.openDelay <= 0) {
			open()
			return
		}

		clearOpenDelay()
		openTimeoutId = window.setTimeout(() => {
			openTimeoutId = null
			open()
		}, props.openDelay)
	}

	const scheduleClose = () => {
		if (effectiveTriggerMode.value !== 'hover') return
		if (hoverPinned.value) return
		clearOpenDelay()

		if (props.closeDelay <= 0) {
			close()
			return
		}

		clearCloseDelay()
		closeTimeoutId = window.setTimeout(() => {
			closeTimeoutId = null
			close()
		}, props.closeDelay)
	}

	const onTriggerClick = () => {
		if (props.disabled) return

		if (effectiveTriggerMode.value === 'click') {
			toggle()
			return
		}

		if (effectiveTriggerMode.value === 'hover') {
			clearDelays()
			if (hoverPinned.value && isOpen.value) {
				close()
				return
			}
			hoverPinned.value = true
			open()
		}
	}

	const onPanelClick = () => {
		if (!props.closeOnContentClick) return
		close()
	}

	const onPanelMouseEnter = () => {
		if (effectiveTriggerMode.value !== 'hover') return
		if (!props.interactive) return
		clearCloseDelay()
	}

	const onPanelMouseLeave = (event: MouseEvent) => {
		const nextTarget = event.relatedTarget
		if (nextTarget instanceof HTMLElement && nextTarget.closest('.floater-panel')) return
		scheduleClose()
	}

	const getAxis = (placement: ResolvedPlacement): 'x' | 'y' => {
		if (placement === 'left' || placement === 'right') return 'x'
		return 'y'
	}

	const getOpposite = (placement: ResolvedPlacement): ResolvedPlacement => {
		const oppositeMap: Record<ResolvedPlacement, ResolvedPlacement> = {
			top: 'bottom',
			bottom: 'top',
			left: 'right',
			right: 'left',
			'top-start': 'bottom-start',
			'top-end': 'bottom-end',
			'bottom-start': 'top-start',
			'bottom-end': 'top-end',
		}
		return oppositeMap[placement]
	}

	const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

	const wouldOverflow = (placement: ResolvedPlacement, anchorRect: DOMRect, panelRect: DOMRect): boolean => {
		switch (placement) {
			case 'top':
			case 'top-start':
			case 'top-end':
				return anchorRect.top - panelRect.height - resolvedOffset.value < VIEWPORT_PADDING
			case 'bottom':
			case 'bottom-start':
			case 'bottom-end':
				return (
					anchorRect.bottom + panelRect.height + resolvedOffset.value > window.innerHeight - VIEWPORT_PADDING
				)
			case 'left':
				return anchorRect.left - panelRect.width - resolvedOffset.value < VIEWPORT_PADDING
			case 'right':
				return anchorRect.right + panelRect.width + resolvedOffset.value > window.innerWidth - VIEWPORT_PADDING
		}
	}

	const resolveAutoPlacement = (placement: Placement, anchorRect: DOMRect): ResolvedPlacement => {
		if (placement === 'auto-start') {
			const bottomSpace = window.innerHeight - anchorRect.bottom - VIEWPORT_PADDING
			const topSpace = anchorRect.top - VIEWPORT_PADDING
			return bottomSpace >= topSpace ? 'bottom-start' : 'top-start'
		}

		if (placement === 'auto-end') {
			const bottomSpace = window.innerHeight - anchorRect.bottom - VIEWPORT_PADDING
			const topSpace = anchorRect.top - VIEWPORT_PADDING
			return bottomSpace >= topSpace ? 'bottom-end' : 'top-end'
		}

		if (placement === 'auto') {
			const spaces: Array<{ placement: ResolvedPlacement; space: number }> = [
				{ placement: 'bottom', space: window.innerHeight - anchorRect.bottom - VIEWPORT_PADDING },
				{ placement: 'top', space: anchorRect.top - VIEWPORT_PADDING },
				{ placement: 'right', space: window.innerWidth - anchorRect.right - VIEWPORT_PADDING },
				{ placement: 'left', space: anchorRect.left - VIEWPORT_PADDING },
			]

			spaces.sort((a, b) => b.space - a.space)
			return spaces[0]?.placement || 'bottom'
		}

		return placement
	}

	const updateArrowPosition = (
		placement: ResolvedPlacement,
		anchorRect: DOMRect,
		panelLeft: number,
		panelTop: number,
		panelRect: DOMRect,
	) => {
		const half = ARROW_SIZE / 2
		const style: Record<string, string> = {}
		const axis = getAxis(placement)

		if (axis === 'y') {
			const isTop = placement.startsWith('top')
			if (isTop) {
				style.bottom = `${-half}px`
				style.borderBottom = `1px solid ${ARROW_BORDER_COLOR}`
				style.borderRight = `1px solid ${ARROW_BORDER_COLOR}`
			} else {
				style.top = `${-half}px`
				style.borderTop = `1px solid ${ARROW_BORDER_COLOR}`
				style.borderLeft = `1px solid ${ARROW_BORDER_COLOR}`
			}

			const anchorCenter = anchorRect.left + anchorRect.width / 2
			const maxLeft = Math.max(panelRect.width - 12, 10)
			const arrowLeft = clamp(anchorCenter - panelLeft - half, 10, maxLeft)
			style.left = `${arrowLeft}px`
		} else {
			const isLeft = placement === 'left'
			if (isLeft) {
				style.right = `${-half}px`
				style.borderRight = `1px solid ${ARROW_BORDER_COLOR}`
				style.borderTop = `1px solid ${ARROW_BORDER_COLOR}`
			} else {
				style.left = `${-half}px`
				style.borderLeft = `1px solid ${ARROW_BORDER_COLOR}`
				style.borderBottom = `1px solid ${ARROW_BORDER_COLOR}`
			}

			const anchorCenter = anchorRect.top + anchorRect.height / 2
			const maxTop = Math.max(panelRect.height - 12, 10)
			const arrowTop = clamp(anchorCenter - panelTop - half, 10, maxTop)
			style.top = `${arrowTop}px`
		}

		arrowStyle.value = style
	}

	const updatePosition = () => {
		if (!isOpen.value || !panelRef.value) return

		const panel = panelRef.value
		const panelRect = panel.getBoundingClientRect()
		const anchor = anchorEl.value

		if (!anchor) {
			close()
			panelStyle.value = {}
			arrowStyle.value = { display: 'none' }
			return
		}

		const anchorRect = anchor.getBoundingClientRect()

		let placement = resolveAutoPlacement(props.placement, anchorRect)
		if (wouldOverflow(placement, anchorRect, panelRect)) {
			const opposite = getOpposite(placement)
			if (!wouldOverflow(opposite, anchorRect, panelRect)) {
				placement = opposite
			}
		}

		effectivePlacement.value = placement

		let top = 0
		let left = 0
		let origin = 'top left'
		const axis = getAxis(placement)

		if (axis === 'y') {
			const isTop = placement.startsWith('top')
			top = isTop
				? anchorRect.top - panelRect.height - resolvedOffset.value
				: anchorRect.bottom + resolvedOffset.value

			if (placement === 'top' || placement === 'bottom') {
				left = anchorRect.left + anchorRect.width / 2 - panelRect.width / 2
			} else if (placement.endsWith('-start')) {
				left = anchorRect.left
			} else {
				left = anchorRect.right - panelRect.width
			}

			origin = isTop ? 'bottom' : 'top'
			if (placement.endsWith('-start')) origin += ' left'
			else if (placement.endsWith('-end')) origin += ' right'
			else origin += ' center'
		} else {
			const isLeft = placement === 'left'
			left = isLeft
				? anchorRect.left - panelRect.width - resolvedOffset.value
				: anchorRect.right + resolvedOffset.value
			top = anchorRect.top + anchorRect.height / 2 - panelRect.height / 2
			origin = isLeft ? 'right center' : 'left center'
		}

		const xOffset = props.strategy === 'absolute' ? window.scrollX : 0
		const yOffset = props.strategy === 'absolute' ? window.scrollY : 0

		if (props.preventOverflow) {
			const minLeft = VIEWPORT_PADDING + xOffset
			const minTop = VIEWPORT_PADDING + yOffset
			const maxLeft = window.innerWidth + xOffset - panelRect.width - VIEWPORT_PADDING
			const maxTop = window.innerHeight + yOffset - panelRect.height - VIEWPORT_PADDING
			left = clamp(left + xOffset, minLeft, maxLeft)
			top = clamp(top + yOffset, minTop, maxTop)
		} else {
			left += xOffset
			top += yOffset
		}

		const styles: Record<string, string> = {
			position: props.strategy,
			top: `${top}px`,
			left: `${left}px`,
			transformOrigin: origin,
			...(resolvedZIndex.value ? { zIndex: resolvedZIndex.value } : {}),
		}

		if (props.matchWidth) {
			styles.width = `${anchorRect.width}px`
		}

		panelStyle.value = styles

		if (props.arrow) {
			updateArrowPosition(placement, anchorRect, left, top, panelRect)
		}
	}

	const focusFirst = () => {
		if (!panelRef.value) return
		const firstFocusable = panelRef.value.querySelector<HTMLElement>(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		)
		firstFocusable?.focus()
	}

	const handleTrapFocus = (event: KeyboardEvent) => {
		if (!props.trapFocus || !panelRef.value || event.key !== 'Tab') return

		const focusables = Array.from(
			panelRef.value.querySelectorAll<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			),
		).filter((el) => !el.hasAttribute('disabled'))

		if (focusables.length === 0) return

		const first = focusables[0]
		const last = focusables[focusables.length - 1]
		const active = document.activeElement as HTMLElement | null

		if (event.shiftKey && active === first) {
			event.preventDefault()
			last.focus()
		} else if (!event.shiftKey && active === last) {
			event.preventDefault()
			first.focus()
		}
	}

	const handleKeydown = (event: KeyboardEvent) => {
		if (!isOpen.value) return

		if (props.closeOnEsc && event.key === 'Escape') {
			event.preventDefault()
			close()
			return
		}

		handleTrapFocus(event)
	}

	const handlePointerDown = (event: PointerEvent) => {
		if (!isOpen.value || !props.closeOnOutside) return

		const target = event.target as Node
		if (panelRef.value?.contains(target)) return
		if (anchorEl.value?.contains(target)) return
		if (target instanceof HTMLElement && target.closest('.floater-panel')) return

		close()
	}

	const addListeners = () => {
		window.addEventListener('resize', updatePosition, { passive: true })
		window.addEventListener('scroll', updatePosition, { capture: true, passive: true })
		document.addEventListener('keydown', handleKeydown)
		document.addEventListener('pointerdown', handlePointerDown)
	}

	const removeListeners = () => {
		window.removeEventListener('resize', updatePosition)
		window.removeEventListener('scroll', updatePosition, true)
		document.removeEventListener('keydown', handleKeydown)
		document.removeEventListener('pointerdown', handlePointerDown)
	}

	const setupObserver = () => {
		disconnectObserver()
		if (typeof ResizeObserver === 'undefined' || !isOpen.value) return

		resizeObserver = new ResizeObserver(() => updatePosition())
		if (panelRef.value) resizeObserver.observe(panelRef.value)
		if (anchorEl.value) resizeObserver.observe(anchorEl.value)
	}

	const slotBindings = computed<FloaterSlotProps>(() => ({
		open,
		close,
		toggle,
		isOpen: isOpen.value,
	}))

	watch(
		() => props.disabled,
		(disabled) => {
			if (disabled) {
				clearDelays()
				close()
			}
		},
	)

	watch(
		isOpen,
		(openState) => {
			if (openState) {
				nextTick(() => {
					updatePosition()
					setupObserver()
					if (props.autoFocus) focusFirst()
				})
				addListeners()
			} else {
				removeListeners()
				disconnectObserver()
			}
		},
		{ immediate: true },
	)

	watch(
		() => [
			props.anchor,
			props.placement,
			props.offset,
			props.offsetDistance,
			props.strategy,
			props.zIndex,
			props.matchWidth,
			props.preventOverflow,
		],
		() => {
			if (!isOpen.value) return
			nextTick(() => {
				updatePosition()
				setupObserver()
			})
		},
	)

	onBeforeUnmount(() => {
		clearDelays()
		removeListeners()
		disconnectObserver()
	})

	defineExpose({
		open,
		close,
		toggle,
		updatePosition,
		isOpen,
		triggerRef,
		panelRef,
	})
</script>

<template>
	<component
		:is="triggerTag"
		v-if="hasTriggerSlot"
		ref="triggerRef"
		v-bind="attrs"
		:class="triggerClass"
		@click="onTriggerClick"
		@mouseenter="scheduleOpen"
		@mouseleave="scheduleClose"
		@focusin="scheduleOpen"
		@focusout="scheduleClose">
		<slot v-bind="slotBindings" />
	</component>

	<component :is="wrapperComponent" v-if="wrapperComponent && hasContentSlot" :to="teleportTarget">
		<Transition :name="transitionName">
			<div
				v-show="isOpen"
				ref="panelRef"
				class="floater-panel"
				:class="panelClass"
				:style="panelStyle"
				:data-placement="effectivePlacement"
				role="dialog"
				aria-modal="false"
				@click="onPanelClick"
				@mouseenter="onPanelMouseEnter"
				@mouseleave="onPanelMouseLeave">
				<div v-if="arrow" class="floater-arrow" :style="arrowStyle" />
				<div class="floater-panel-content" :class="contentClass">
					<slot name="content" v-bind="slotBindings" />
				</div>
			</div>
		</Transition>
	</component>

	<Transition v-else-if="hasContentSlot" :name="transitionName">
		<div
			v-show="isOpen"
			ref="panelRef"
			class="floater-panel"
			:class="panelClass"
			:style="panelStyle"
			:data-placement="effectivePlacement"
			role="dialog"
			aria-modal="false"
			@click="onPanelClick"
			@mouseenter="onPanelMouseEnter"
			@mouseleave="onPanelMouseLeave">
			<div v-if="arrow" class="floater-arrow" :style="arrowStyle" />
			<div class="floater-panel-content" :class="contentClass">
				<slot name="content" v-bind="slotBindings" />
			</div>
		</div>
	</Transition>
</template>

<style scoped>
	@reference '@/styles/tailwind.css';

	.floater-panel {
		@apply bg-base-100 text-base-content border-neutral/50 fixed z-60 min-w-24 rounded-xl border p-2;
		box-shadow:
			0 8px 24px -8px rgba(0, 0, 0, 0.25),
			0 2px 8px -4px rgba(0, 0, 0, 0.15);
	}

	.floater-panel-content {
		position: relative;
		z-index: 1;
	}

	.floater-arrow {
		@apply bg-base-100;
		position: absolute;
		width: 8px;
		height: 8px;
		transform: rotate(45deg);
		z-index: 0;
	}

	.floater-slide-down-enter-active,
	.floater-slide-down-leave-active {
		transition:
			opacity 150ms ease,
			transform 150ms ease;
	}

	.floater-slide-down-enter-from,
	.floater-slide-down-leave-to {
		opacity: 0;
		transform: scale(0.97) translateY(-4px);
	}

	.floater-slide-up-enter-active,
	.floater-slide-up-leave-active {
		transition:
			opacity 150ms ease,
			transform 150ms ease;
	}

	.floater-slide-up-enter-from,
	.floater-slide-up-leave-to {
		opacity: 0;
		transform: scale(0.97) translateY(4px);
	}

	.floater-slide-left-enter-active,
	.floater-slide-left-leave-active {
		transition:
			opacity 150ms ease,
			transform 150ms ease;
	}

	.floater-slide-left-enter-from,
	.floater-slide-left-leave-to {
		opacity: 0;
		transform: scale(0.97) translateX(4px);
	}

	.floater-slide-right-enter-active,
	.floater-slide-right-leave-active {
		transition:
			opacity 150ms ease,
			transform 150ms ease;
	}

	.floater-slide-right-enter-from,
	.floater-slide-right-leave-to {
		opacity: 0;
		transform: scale(0.97) translateX(-4px);
	}
</style>
